import { Location } from '@angular/common';
import { afterNextRender, Component, ElementRef, inject, Injector, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, timeout, TimeoutError } from 'rxjs';

import { Audit } from 'src/app/models/audit/audit';
import { AuditService } from 'src/app/services/audit.service';
import { UserService } from 'src/app/services/user.service';
import { AuditFormFactory } from 'src/app/utils/factories/auditformFactory';
import { resolveAuditInvalidFieldTabIndex } from 'src/app/utils/audit-invalid-field-navigation.helper';
import { getApiErrorDisplayText } from 'src/app/utils/api-error.util';
import { navigateToInvalidField } from 'src/app/utils/invalid-field-navigation.helper';
import {
  flushPendingInvalidFieldNavigation,
  requestInvalidFieldNavigation
} from 'src/app/utils/pending-invalid-field-navigation.helper';
import { v4 as uuid } from 'uuid';

import { Auditor } from '../../models/auditor';
import { Organization } from '../../models/organization';
import { State } from '../../models/states';
import { User } from '../../models/user';
import { AccessService } from '../../services/access.service';
import { OrganizationService } from '../../services/organization.service';
import { observableNext } from '../../utils';
import { BeforeCloseComponent } from '../before-close/before-close.component';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { HeaderComponent } from '../header/header.component';
import { InvalidFormSummaryComponent } from '../invalid-form-summary/invalid-form-summary.component';

import { AuditAnnouncementsComponent } from './audit-announcements/audit-announcements.component';
import { AuditBasicsComponent } from './audit-basics/audit-basics.component';
import {
  AUDIT_SAVE_SUCCESS_MESSAGE,
  AUDIT_SAVE_TIMEOUT_MESSAGE,
  AUDIT_SAVE_TIMEOUT_MS
} from '../../constants/audit-detail-save.constants';
import { INVALID_FORM_FIELD_LABELS } from '../../constants/audit-detail-form-validation.constants';
import { DEFAULT_SNACKBAR_DURATION_MS, SUCCESS_SNACKBAR_DURATION_MS } from '../../constants/snackbar.constants';
import {
  AUDIT_EMAIL_WARNING_DELAY_MS,
  AUDIT_EMAIL_WARNING_DURATION_MS
} from '../../constants/audit-detail-email-warning.constants';
import {
  buildInvalidFormState,
  InvalidEnabledField,
  logInvalidEnabledControls,
  shouldWarnInvalidFields
} from '../../utils/form-validation.helper';
import { showWarningSnackbars } from '../../utils/warning-snackbar.util';
import { AuditLogComponent } from './audit-log/audit-log.component';
import { AuditRecommendationsComponent } from './audit-recommendations/audit-recommendations.component';
import { AuditResponsiblesComponent } from './audit-responsibles/audit-responsibles.component';

@Component({
  selector: 'app-audit-detail',
  templateUrl: './audit-detail.component.html',
  styleUrls: ['./audit-detail.component.scss'],
  imports: [
    HeaderComponent,
    MatButtonModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    MatTabsModule,
    AuditRecommendationsComponent,
    AuditBasicsComponent,
    AuditResponsiblesComponent,
    FileUploadComponent,
    InvalidFormSummaryComponent,
    MatCardModule,
    AuditAnnouncementsComponent,
    AuditLogComponent
  ]
})
export class AuditDetailComponent implements OnInit {
  private auditService = inject(AuditService);
  private organizationService = inject(OrganizationService);
  private location = inject(Location);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private elementRef = inject(ElementRef<HTMLElement>);
  private injector = inject(Injector);
  private snackBar = inject(MatSnackBar);
  private auditFactory = inject(AuditFormFactory);
  dialog = inject(MatDialog);
  private accessService = inject(AccessService);
  userService = inject(UserService);

  tabPage = 0;
  audit!: Audit;
  currentDate = new Date();
  fileLinkId!: string;
  organizations!: Organization[];
  users!: User[];
  error = '';
  invalidFields: InvalidEnabledField[] = [];
  private pendingInvalidFieldNavigation?: string;
  saving = false;
  new = false;
  loaded = false;
  nav!: string;
  hasFormChanged = false;
  isGPIA = false;
  formBuilder = inject(NonNullableFormBuilder);

  // TODO: Skipped for migration because:
  //  There are references to this query that cannot be migrated automatically.
  @ViewChildren(FileUploadComponent) fileUploader!: QueryList<FileUploadComponent>;

  form!: FormGroup;

  companyAutoneum = '';

  ngOnInit() {
    if (this.route.snapshot.params?.area) {
      this.isGPIA = this.route.snapshot.params?.area === 'GPIA';
      this.new = true;
    }

    this.getAllValues();
    this.tabPage = Number(this.route.snapshot.params?.tab);
  }

  goBack() {
    this.location.back();
  }

  getAllValues() {
    this.loadOrganizations();
    if (this.new) {
      this.initializeNewAudit();
      return;
    }

    this.loadExistingAudit();
  }

  sortOrganizationByAbbreviation(org1: Organization, org2: Organization): number {
    return org1.abbreviation < org2.abbreviation ? 1 : -1;
  }

  chkEditable(): boolean {
    if (this.new) return true;
    switch (this.audit?.auditor) {
      case Auditor.GPIA:
        return this.accessService.isGpiaAuditor();
      case Auditor.GPRM:
        return this.accessService.isGprmAuditor();
      default:
        return false;
    }
  }

  isDraft() {
    return this.form.value.status === State.DRAFT;
  }

  showCompanySelect() {
    return this.chkEditable() && this.isDraft();
  }

  chkError(formName: string) {
    const field = this.form.controls[formName];
    return field && field.errors && field.touched;
  }

  canSave() {
    return this.chkEditable();
  }

  // End View Contorl

  createForm() {
    this.nav = this.resolveNavigationKey();
    this.form = this.auditFactory.build(
      this.formBuilder,
      this.chkEditable(),
      this.isGPIA ? 'GPIA' : 'GPRM',
      this.audit
    );
    this.onChanges();
  }

  isDefined(): boolean {
    return this.loaded;
  }

  isAudit(audit: string): boolean {
    return this.form.value.auditor === audit;
  }

  updateTabs(evt: number) {
    this.tabPage = evt;
    this.router.navigate([`../${evt}`], {
      relativeTo: this.route,
      replaceUrl: true
    });

    this.flushPendingInvalidFieldNavigation();
  }

  /**
   * Validates the form and saves the audit. On API error, displays the error message and reason (e.g. permissions) in a snackbar.
   * @param close - If true, navigates back after successful save.
   */
  save(close = false): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.form.updateValueAndValidity();
    this.form.enable();
    if (!this.form.valid) {
      this.invalidForm();
      return;
    }

    const audit = this.prepareAuditForSave();
    this.auditService
      .postAudit(audit)
      .pipe(
        timeout(AUDIT_SAVE_TIMEOUT_MS),
        finalize(() => {
          this.saving = false;
        })
      )
      .subscribe({
        error: (err) => {
          this.showSaveError(err);
        },
        next: (response) => {
          this.handleSaveSuccess(response, close);
        }
      });
    this.hasFormChanged = false;
  }

  private prepareAuditForSave(): Audit {
    const audit: Audit = this.form.value;
    audit.reportingYear = audit.reportingYear.toString();
    return audit;
  }

  private showSaveError(err: unknown): void {
    const message = err instanceof TimeoutError ? AUDIT_SAVE_TIMEOUT_MESSAGE : getApiErrorDisplayText(err);

    this.snackBar.open(message, undefined, {
      panelClass: 'error',
      duration: DEFAULT_SNACKBAR_DURATION_MS
    });
  }

  private handleSaveSuccess(response: { audit: Audit; emailWarnings?: string[] }, close: boolean): void {
    this.audit = response.audit;
    this.hasFormChanged = false;
    this.form.controls['newCreatedBy'].setValue('');
    this.createForm();

    this.snackBar.open(AUDIT_SAVE_SUCCESS_MESSAGE, undefined, {
      duration: SUCCESS_SNACKBAR_DURATION_MS
    });

    this.showEmailWarnings(response.emailWarnings);
    this.handlePostSaveNavigation(close);
  }

  private showEmailWarnings(emailWarnings?: string[]): void {
    showWarningSnackbars(
      this.snackBar,
      emailWarnings,
      AUDIT_EMAIL_WARNING_DURATION_MS,
      AUDIT_EMAIL_WARNING_DELAY_MS
    );
  }

  private handlePostSaveNavigation(close: boolean): void {
    if (this.new) {
      this.router.navigate(['/audits/', this.form.controls['auditor'].value]);
    }
    if (close) {
      this.location.back();
    }
  }

  invalidForm(): void {
    this.form.markAllAsTouched();
    const invalidFormState = buildInvalidFormState(this.form, INVALID_FORM_FIELD_LABELS);
    this.invalidFields = invalidFormState.invalidFields;
    this.error = invalidFormState.error;
    const invalidFieldLabels = this.invalidFields.map((field) => field.label);
    logInvalidEnabledControls(this.form);

    this.snackBar.open(this.error, undefined, {
      panelClass: 'error',
      duration: DEFAULT_SNACKBAR_DURATION_MS
    });

    if (shouldWarnInvalidFields(invalidFieldLabels)) {
      console.warn('Invalid fields:', invalidFieldLabels);
    }

    this.saving = false;
  }

  onChanges(): void {
    this.form.valueChanges.subscribe(() => {
      this.hasFormChanged = true;
      if (!this.form.invalid) {
        this.error = '';
        this.invalidFields = [];
      }
      if (this.form.get('status')?.value === State.DRAFT && this.showCompleteResponsibles()) {
        this.completeResponsibles();
      }
    });
  }

  openDialog() {
    if (this.hasFormChanged) {
      this.form.enable();
      const dialogRef = this.dialog.open(BeforeCloseComponent, {
        data: {
          formGroup: this.form
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.save(true);
        }
      });
    } else {
      this.location.back();
    }
  }

  showCompleteResponsibles(): boolean {
    return (
      this.form.value.assessmentCoordinator &&
      this.form.value.legalUnit &&
      this.form.value.status !== 'Pending' &&
      this.form.value.status !== 'Settled' &&
      this.form.value.status !== 'Finally settled'
    );
  }

  completeResponsibles() {
    this.form.controls['status'].setValue('Pending');
  }

  private loadOrganizations(): void {
    this.organizationService.getCompanies().subscribe({
      next: (orgs) => (this.organizations = orgs.sort(this.sortOrganizationByAbbreviation))
    });
  }

  private initializeNewAudit(): void {
    this.createForm();
    this.form.enable();
    this.audit = this.form.value as Audit;
    this.fileLinkId = this.audit.fileLinkId;
    this.loaded = true;
  }

  private loadExistingAudit(): void {
    this.auditService.getDataById(this.route.snapshot.params.id).subscribe({
      next: (auditEx) => {
        this.ensureFileLinkId(auditEx);
        this.audit = auditEx;
        this.fileLinkId = auditEx.fileLinkId;
        this.createForm();
        this.loaded = true;
      }
    });
  }

  private ensureFileLinkId(auditEx: Audit): void {
    if (!auditEx.fileLinkId) {
      this.fileLinkId = auditEx.fileLinkId = uuid();
      return;
    }

    this.fileLinkId = auditEx.fileLinkId;
    observableNext<QueryList<FileUploadComponent>>(this.fileUploader.changes, (v) => {
      if (v.length !== 1) {
        return false;
      }
      v.toArray()[0].loadFiles();
      return true;
    });
  }

  private resolveNavigationKey(): string {
    if (this.new) {
      return this.isGPIA ? '1' : '2';
    }

    return this.audit?.auditor === Auditor.GPIA ? '1' : '2';
  }

  goToInvalidField(fieldName: string): void {
    requestInvalidFieldNavigation({
      fieldName,
      targetTab: resolveAuditInvalidFieldTabIndex(fieldName, this.isDraft()),
      currentTab: this.tabPage,
      setPendingField: (pendingFieldName) => this.setPendingInvalidFieldNavigation(pendingFieldName),
      activateTab: (tabIndex) => this.activateTab(tabIndex),
      navigateWithinCurrentTab: (fieldNameToNavigate) => this.navigateWithinCurrentTab(fieldNameToNavigate)
    });
  }

  private navigateWithinCurrentTab(fieldName: string): void {
    navigateToInvalidField({
      fieldName,
      rootElement: this.elementRef.nativeElement,
      querySelector: '[formcontrolname="{fieldName}"]'
    });
  }

  private activateTab(tabIndex: number): void {
    this.tabPage = tabIndex;
    this.router.navigate([`../${tabIndex}`], {
      relativeTo: this.route,
      replaceUrl: true
    });

    this.flushPendingInvalidFieldNavigation();
  }

  private flushPendingInvalidFieldNavigation(): void {
    flushPendingInvalidFieldNavigation({
      pendingField: this.pendingInvalidFieldNavigation,
      clearPendingField: () => this.clearPendingInvalidFieldNavigation(),
      scheduleAfterRender: (callback) => this.scheduleAfterNextRender(callback),
      navigateWithinCurrentTab: (fieldName) => this.navigateWithinCurrentTab(fieldName)
    });
  }

  private setPendingInvalidFieldNavigation(fieldName: string): void {
    this.pendingInvalidFieldNavigation = fieldName;
  }

  private clearPendingInvalidFieldNavigation(): void {
    this.pendingInvalidFieldNavigation = undefined;
  }

  private scheduleAfterNextRender(callback: () => void): void {
    afterNextRender(callback, { injector: this.injector });
  }
}
